import { Platform } from 'react-native';
import PantryTypes from '../../constants/PantryTypes';
import { isPlentifulProvider } from '../../Location';
import { logError } from '../../utils/logger';

/* eslint-disable global-require, import/no-dynamic-require -- React Native
 * bundles image assets through static require() calls that Metro resolves at
 * build time; this is the required idiom for a platform-specific asset map.
 */

// cross-platform asset path resolution doesn't like the ../../ here
const ICON_PATH = 'assets/images/marker_icons';

const MARKER_ICON =
  Platform.OS === 'web'
    ? {
        plentiful_pantry_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_qr.svg`),
        plentiful_pantry_non_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_non_qr.svg`),
        other_pantry_verified: require(`../../${ICON_PATH}/category/other_pantry_verified.svg`),
        other_pantry_unverified: require(`../../${ICON_PATH}/category/other_pantry_unverified.svg`),
        hot_meal_verified: require(`../../${ICON_PATH}/category/hot_meal_verified.svg`),
        hot_meal_unverified: require(`../../${ICON_PATH}/category/hot_meal_unverified.svg`),
        non_food_verified: require(`../../${ICON_PATH}/category/non_food_verified.svg`),
        non_food_unverified: require(`../../${ICON_PATH}/category/non_food_unverified.svg`),
        fano_affiliate_verified: require(`../../${ICON_PATH}/category/fano_affiliate_verified.svg`),
      }
    : {
        plentiful_pantry_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_qr.png`),
        plentiful_pantry_non_qr: require(`../../${ICON_PATH}/category/plentiful_pantry_non_qr.png`),
        other_pantry_verified: require(`../../${ICON_PATH}/category/other_pantry_verified.png`),
        other_pantry_unverified: require(`../../${ICON_PATH}/category/other_pantry_unverified.png`),
        hot_meal_verified: require(`../../${ICON_PATH}/category/hot_meal_verified.png`),
        hot_meal_unverified: require(`../../${ICON_PATH}/category/hot_meal_unverified.png`),
        non_food_verified: require(`../../${ICON_PATH}/category/non_food_verified.png`),
        non_food_unverified: require(`../../${ICON_PATH}/category/non_food_unverified.png`),
        // Native FANO asset deferred — fall back to other_pantry_verified for now.
        fano_affiliate_verified: require(`../../${ICON_PATH}/category/other_pantry_verified.png`),
      };

// Map marker icons for PantryMap and PantryDetails components
const MAP_MARKER_ICONS =
  Platform.OS === 'web'
    ? {
        inActive: require('../../assets/images/web-inactive_pin_small.png'), // needs 28x34
        active: require('../../assets/images/web-active_pin_small.png'), // 28x47
        otherPantry: require('../../assets/images/web-otherpantry_pin_small.png'), // brand dark blue for null/0 IDs
        fanoAffiliate: require('../../assets/images/web-fano_pin_small.svg'), // Feeding America Network Organization
        freshtrak: require('../../assets/images/web-freshtrak_pin_small.png'), // green — FreshTrak static overlay (recoloured otherpantry pin)
      }
    : {
        // Note: we've had mixed results with the size of the marker relying on just the size of the image to scale
        // correctly for the device resolution.
        // cf. https://github.com/PlentifulApp/plentiful/issues/1948
        inActive: require('../../assets/images/mobile-native-inactive_pin_small.png'), // 66x81
        active: require('../../assets/images/mobile-native-active_pin_small.png'), // 56x81
        otherPantry: require('../../assets/images/mobile-native-otherpantry_pin_small.png'), // brand dark blue for null/0 IDs
        // Native FANO asset deferred — fall back to otherPantry pin for now.
        fanoAffiliate: require('../../assets/images/mobile-native-otherpantry_pin_small.png'),
        // Native FreshTrak asset deferred — the demo targets web. Point at the
        // existing otherPantry PNG rather than a not-yet-designed green asset.
        freshtrak: require('../../assets/images/mobile-native-otherpantry_pin_small.png'),
      };

const isFanoAffiliated = (pantry) =>
  Array.isArray(pantry?.affiliations) && pantry.affiliations.includes('FANO');

/**
 * Get the appropriate map marker icon for PantryMap and PantryDetails components.
 * This uses the simple icon system that works reliably across platforms.
 */
export const getMapMarkerIcon = (pantry, selectedPantryId = null) => {
  if (pantry.id === selectedPantryId) {
    return MAP_MARKER_ICONS.active;
  }

  if (pantry.source === 'freshtrak') {
    return MAP_MARKER_ICONS.freshtrak;
  }

  if (isPlentifulProvider(pantry)) {
    return MAP_MARKER_ICONS.inActive;
  }

  if (isFanoAffiliated(pantry)) {
    return MAP_MARKER_ICONS.fanoAffiliate;
  }
  return MAP_MARKER_ICONS.otherPantry;
};

/**
 * Using the data returned from the API, choose the most appropriate marker icon. This categorization
 * logic must match the logic in the backend, currently in `searchAlgoliaPantryIndex`.
 */
export const getMarkerIcon = (pantry) => {
  if (pantry.type === PantryTypes.plentiful_provider) {
    return pantry.use_qr_codes ? MARKER_ICON.plentiful_pantry_qr : MARKER_ICON.plentiful_pantry_non_qr;
  }

  if (pantry.pantry_type === PantryTypes.plentiful_provider) {
    return pantry.use_qr_codes ? MARKER_ICON.plentiful_pantry_qr : MARKER_ICON.plentiful_pantry_non_qr;
  }

  // Lighthouse rows arrive without pantry_type. Distinguish FANO-affiliated from
  // generic Lighthouse rows before falling through to the legacy Plentiful types.
  if (isFanoAffiliated(pantry)) {
    return MARKER_ICON.fano_affiliate_verified;
  }

  if (pantry.pantry_type === PantryTypes.other_pantry) {
    return pantry.verified ? MARKER_ICON.other_pantry_verified : MARKER_ICON.other_pantry_unverified;
  }

  if (pantry.pantry_type === PantryTypes.hot_meal) {
    return pantry.verified ? MARKER_ICON.hot_meal_verified : MARKER_ICON.hot_meal_unverified;
  }

  if (pantry.pantry_type === PantryTypes.non_food) {
    return pantry.verified ? MARKER_ICON.non_food_verified : MARKER_ICON.non_food_unverified;
  }

  // Lighthouse rows with no FANO tag and no pantry_type land here — render with
  // the verified-other-pantry icon (formerly the "blue pin" slot).
  if (pantry && pantry.id != null && pantry.pantry_id != null && !isPlentifulProvider(pantry)) {
    return MARKER_ICON.other_pantry_verified;
  }

  logError(new Error(`Unknown pantry type: ${pantry.pantry_type} for pantry ${pantry.id}`));
  return MAP_MARKER_ICONS.otherPantry;
};
